import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Circle } from "lucide-react";
import { api, GATE_EVENT } from "./api.js";
import { useAuth } from "../auth.jsx";

// Account onboarding: company details + a verified email and phone number (and billing) are
// required before anything can be deployed. The server enforces it; this context only lets
// the UI say so BEFORE the customer fills in a form that will be refused.
//
// status:
//   "loading"  first fetch in flight
//   "ready"    `data` is the server's onboarding object
//   "unknown"  the call failed (older server, 404, offline) — never block on this
//   "skipped"  an operator on their own account: onboarding does not apply to them
export const COMPANY_DETAILS_PATH = "/settings#company-details";
export const BILLING_PATH = "/wallet";

const DETAIL_LABEL = { company: "company name", address: "address", country: "country", email: "contact email", phone: "phone number" };
const listMissing = (missing) => (Array.isArray(missing) ? missing : []).map((m) => DETAIL_LABEL[m] || m);

// The four things the checklist shows, from the onboarding object.
export function checklistFor(data) {
  if (!data) return [];
  const missing = listMissing(data.missing);
  const detailsDone = Array.isArray(data.missing) ? missing.length === 0 : !!data.complete;
  const items = [
    {
      key: "details", label: "Company details", done: detailsDone, to: COMPANY_DETAILS_PATH,
      detail: detailsDone ? null : missing.length ? `Still needed: ${missing.join(", ")}` : "Add your company name, address, country, email and phone",
    },
    {
      key: "email", label: "Email verified", done: !!data.email?.verified, to: COMPANY_DETAILS_PATH,
      detail: data.email?.verified ? null : data.email?.address ? `Enter the code we send to ${data.email.address}` : "Add a contact email, then verify it",
    },
    {
      key: "phone", label: "Phone verified", done: !!data.phone?.verified, to: COMPANY_DETAILS_PATH,
      detail: data.phone?.verified ? null : data.phone?.number ? `Verify ${data.phone.number}` : "Add a phone number, then verify it",
    },
  ];
  if (data.billing) {
    items.push({
      key: "billing", label: "Billing ready", done: !!data.billing.ready, to: BILLING_PATH,
      detail: data.billing.ready ? null : data.billing.reason || "Start a subscription or add account credit",
    });
  }
  return items;
}

// Same rows, built from a refused action (403 onboarding_required) instead of the full object.
// The refusal only says what is wrong, so only the outstanding rows are listed.
export function checklistFromGate(gate) {
  const items = [];
  const missing = listMissing(gate?.missing);
  if (missing.length) items.push({ key: "details", label: "Company details", done: false, to: COMPANY_DETAILS_PATH, detail: `Still needed: ${missing.join(", ")}` });
  if (gate?.emailVerified === false) items.push({ key: "email", label: "Email verified", done: false, to: COMPANY_DETAILS_PATH, detail: "Verify your contact email" });
  if (gate?.phoneVerified === false) items.push({ key: "phone", label: "Phone verified", done: false, to: COMPANY_DETAILS_PATH, detail: "Verify your phone number" });
  return items;
}

export function ChecklistRows({ items, onNavigate, compact = false }) {
  if (!items?.length) return null;
  return (
    <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: compact ? "row" : "column", flexWrap: "wrap", gap: compact ? "4px 14px" : 9 }}>
      {items.map((it) => (
        <li key={it.key} style={{ display: "flex", alignItems: "flex-start", gap: 8, minWidth: 0, fontSize: compact ? 12.5 : 13 }}>
          {it.done
            ? <CheckCircle2 size={compact ? 14 : 16} style={{ color: "var(--ok)", flexShrink: 0, marginTop: compact ? 2 : 1 }} aria-hidden="true" />
            : <Circle size={compact ? 14 : 16} style={{ color: "var(--warn)", flexShrink: 0, marginTop: compact ? 2 : 1 }} aria-hidden="true" />}
          <span style={{ minWidth: 0 }}>
            <span style={{ fontWeight: 600, color: "var(--text)" }}>{it.label}</span>
            <span className="sr-only">{it.done ? " — done" : " — to do"}</span>
            {!compact && !it.done && it.detail && (
              <span style={{ display: "block", color: "var(--text-muted)", fontSize: 12.5, marginTop: 1, overflowWrap: "anywhere" }}>
                {it.detail}{" "}
                {it.to && <Link to={it.to} onClick={onNavigate} style={{ color: "var(--accent-text)", fontWeight: 600, whiteSpace: "nowrap" }}>{it.key === "billing" ? "Set up billing" : "Fix this"}</Link>}
              </span>
            )}
          </span>
        </li>
      ))}
    </ul>
  );
}

const OnboardingContext = createContext(null);
const FALLBACK = { status: "unknown", data: null, supportOrg: null, incomplete: false, blocked: false, applies: false, items: [], refresh: async () => null };

export function OnboardingProvider({ children }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const userKey = user ? `${user.id ?? user.email ?? "user"}:${user.role || ""}` : null;
  const [state, setState] = useState({ status: "loading", data: null, supportOrg: null });
  const seq = useRef(0);
  const lastLoad = useRef(0);

  const refresh = useCallback(async () => {
    if (!userKey) return null;
    const n = ++seq.current;
    lastLoad.current = Date.now();
    try {
      let data;
      let supportOrg = null;
      if (isAdmin) {
        // Operators are not customers: onboarding only matters while they are scoped into a
        // client's account through a support session, and then it is the CLIENT's state.
        const s = await api.supportSession();
        if (!s?.active || s.org_id == null) {
          if (n === seq.current) setState({ status: "skipped", data: null, supportOrg: null });
          return null;
        }
        supportOrg = { id: s.org_id, name: s.org_name || `Org ${s.org_id}` };
        data = await api.adminOrgOnboarding(s.org_id);
      } else {
        data = await api.orgOnboarding();
      }
      if (n !== seq.current) return null;
      if (!data || typeof data !== "object") { setState({ status: "unknown", data: null, supportOrg }); return null; }
      setState({ status: "ready", data, supportOrg });
      return data;
    } catch {
      // 404 on a server that predates onboarding, a 5xx, offline: unknown is not "incomplete".
      if (n === seq.current) setState({ status: isAdmin ? "skipped" : "unknown", data: null, supportOrg: null });
      return null;
    }
  }, [userKey, isAdmin]);

  useEffect(() => { refresh(); }, [refresh]);

  // Stay honest without polling: re-check when the tab regains focus (the customer may have
  // paid in another tab, or the operator may have verified their number by hand), and
  // straight after any action is refused for onboarding.
  useEffect(() => {
    if (!userKey) return undefined;
    const onFocus = () => { if (document.visibilityState !== "hidden" && Date.now() - lastLoad.current > 30_000) refresh(); };
    const onGate = (e) => { if (e.detail?.code === "onboarding_required") refresh(); };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);
    window.addEventListener(GATE_EVENT, onGate);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onFocus);
      window.removeEventListener(GATE_EVENT, onGate);
    };
  }, [userKey, refresh]);

  const value = useMemo(() => {
    const { status, data, supportOrg } = state;
    const incomplete = status === "ready" && data?.complete === false && !data?.exempt;
    return {
      status, data, supportOrg, refresh,
      // Does onboarding apply to whoever is looking at the screen right now?
      applies: status !== "skipped",
      // Only a definite "incomplete and not exempt" counts. Loading and unknown never do.
      incomplete,
      // Close the create forms. This includes an operator inside a support session: the
      // server holds a support session to the CLIENT's standing, so the same refusal applies.
      // A client who predates the gate is let through until their grace date (server-side
      // too), so the forms stay open and only the banner nags.
      blocked: incomplete && !data?.graceUntil,
      graceUntil: incomplete ? data?.graceUntil || null : null,
      items: checklistFor(data),
    };
  }, [state, refresh]);

  return <OnboardingContext.Provider value={value}>{children}</OnboardingContext.Provider>;
}

export function useOnboarding() {
  return useContext(OnboardingContext) || FALLBACK;
}
